import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../utils/init-firebase';
import { COLLECTION_NAMES, Issue, Rock, Milestone } from '../types';

// ============================================================================
// Rock Status Validation Functions (New)
// ============================================================================

/**
 * Get the count of open issues for a specific rock
 * @param rockId - The ID of the rock
 * @returns Promise resolving to the count of open issues
 */
export async function getOpenIssuesCount(rockId: string): Promise<number> {
  try {
    // Firestore doesn't support != queries efficiently, so we query for open and in-progress statuses
    const openIssuesQuery = query(
      collection(db, COLLECTION_NAMES.ISSUES),
      where('relatedRockId', '==', rockId),
      where('status', 'in', ['open', 'in-progress'])
    );
    const snapshot = await getDocs(openIssuesQuery);
    return snapshot.size;
  } catch (error) {
    console.error('Error getting open issues count:', error);
    throw error;
  }
}

/**
 * Get all open issues for a specific rock
 * @param rockId - The ID of the rock
 * @returns Promise resolving to an array of open issues
 */
export async function getOpenIssuesForRock(rockId: string): Promise<Issue[]> {
  try {
    // Firestore doesn't support != queries efficiently, so we query for open and in-progress statuses
    const issuesQuery = query(
      collection(db, COLLECTION_NAMES.ISSUES),
      where('relatedRockId', '==', rockId),
      where('status', 'in', ['open', 'in-progress'])
    );
    const snapshot = await getDocs(issuesQuery);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Issue[];
  } catch (error) {
    console.error('Error getting open issues for rock:', error);
    throw error;
  }
}

/**
 * Validate if a rock status update is allowed
 * Note: This is used for UI flow validation (showing modals), not for actual status changes.
 * Status is now computed from open issues count.
 * @param rockId - The ID of the rock
 * @param newStatus - The new status to set ('on-track' or 'off-track')
 * @returns Promise resolving to validation result with error message if invalid
 */
export async function validateRockStatusUpdate(
  rockId: string,
  newStatus: 'on-track' | 'off-track'
): Promise<{ valid: boolean; error?: string }> {
  try {
    const openIssuesCount = await getOpenIssuesCount(rockId);
    
    // When trying to mark as on-track, require all issues to be resolved
    if (newStatus === 'on-track') {
      if (openIssuesCount > 0) {
        return {
          valid: false,
          error: `Cannot mark rock as on-track. There ${openIssuesCount === 1 ? 'is' : 'are'} ${openIssuesCount} open ${openIssuesCount === 1 ? 'issue' : 'issues'} that must be resolved first.`,
        };
      }
      return { valid: true };
    }
    
    // When trying to mark as off-track, require at least 1 issue
    if (newStatus === 'off-track') {
      if (openIssuesCount === 0) {
        return {
          valid: false,
          error: 'An issue must be created before a Rock can be moved to Off Track status. Off Track Rocks require documented issues to provide visibility into blockers and help the team understand what\'s preventing progress.',
        };
      }
      return { valid: true };
    }

    return { valid: true };
  } catch (error) {
    console.error('Error validating rock status update:', error);
    // On error, allow the update but log it
    // This prevents blocking users due to transient errors
    return { valid: true };
  }
}

/**
 * Compute rock status based on open issues count
 * Core principle: Rock with any open issues = off-track, Rock with no open issues = on-track
 * @param openIssuesCount - The count of open issues for the rock
 * @returns 'on-track' if no open issues, 'off-track' if any open issues
 */
export function computeRockStatus(openIssuesCount: number): 'on-track' | 'off-track' {
  return openIssuesCount > 0 ? 'off-track' : 'on-track';
}

/**
 * Get computed rock status from rock and its issues
 * Filters issues to open/in-progress status and computes status based on count
 * @param rock - The rock object
 * @param openIssues - Array of issues for the rock (should already be filtered to open/in-progress)
 * @returns Computed status: 'on-track' if no open issues, 'off-track' if any open issues
 */
export function getComputedRockStatus(rock: Rock, openIssues: Issue[]): 'on-track' | 'off-track' {
  // Filter to only open/in-progress issues (in case the array wasn't pre-filtered)
  const filteredIssues = openIssues.filter(
    (issue) => issue.status === 'open' || issue.status === 'in-progress'
  );
  return computeRockStatus(filteredIssues.length);
}

// ============================================================================
// Existing Utility Functions
// ============================================================================

/**
 * Calculate current quarter (Q1-Q4)
 * @returns Quarter string (e.g., "Q1", "Q2", "Q3", "Q4")
 */
export function calculateQuarter(): string {
  const now = new Date();
  const month = now.getMonth(); // 0-11
  const quarter = Math.floor(month / 3) + 1; // 1-4
  return `Q${quarter}`;
}

/**
 * Calculate current week within the quarter (1-13)
 * @returns Object with current week and total weeks (13)
 */
export function calculateWeek(): { current: number; total: number } {
  const now = new Date();
  const startOfYear = new Date(now.getFullYear(), 0, 1);
  const diffTime = now.getTime() - startOfYear.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  const weekNumber = Math.ceil((diffDays + startOfYear.getDay() + 1) / 7);
  const weekInQuarter = ((weekNumber - 1) % 13) + 1;
  return { current: weekInQuarter, total: 13 };
}

/**
 * Calculate milestone progress percentage
 * @param milestones - Array of milestones
 * @returns Percentage (0-100) of completed milestones
 */
export function calculateMilestoneProgress(milestones: Milestone[]): number {
  if (!milestones || milestones.length === 0) return 0;
  const completed = milestones.filter((m) => m.complete).length;
  return Math.round((completed / milestones.length) * 100);
}

/**
 * Get collaborator type for a user in a rock
 * Returns 'R,T' if user is both rock collaborator and task assignee
 * @param rock - The rock object
 * @param userId - The user ID to check
 * @returns String indicating collaborator type ('R,T' or '')
 */
export function getCollaboratorType(rock: Rock, userId: string): string {
  const isRockCollab = rock.collaborators?.includes(userId);
  return isRockCollab ? 'R,T' : '';
}

/**
 * Format collaborator types array to comma-separated string
 * @param types - Array of collaborator types ('R' | 'T')[]
 * @returns Comma-separated string (e.g., "R,T" or "R" or "T")
 */
export function formatCollaboratorTypes(types: ('R' | 'T')[]): string {
  return types.join(',');
}

/**
 * Calculate average AI score from rocks and their scores
 * @param rocks - Array of Rock objects
 * @param rockAiScores - Record mapping rock IDs to their AI scores
 * @returns Average score or null if no valid scores
 */
export function calculateAverageScore(
  rocks: { id: string }[],
  rockAiScores: Record<string, number>
): number | null {
  const scores = rocks
    .map((rock) => rockAiScores[rock.id])
    .filter((score): score is number => score !== null && score !== undefined && !isNaN(score));
  
  if (scores.length === 0) return null;
  const sum = scores.reduce((acc, score) => acc + score, 0);
  return Math.round(sum / scores.length);
}

/**
 * Get color for AI score based on tier
 * Score Tiers:
 * - Green (70-100): Strong
 * - Yellow (50-69): Needs Improvement
 * - Red (0-49): Critical Issues
 * @param score - AI score (0-100)
 * @returns Chakra UI color string
 */
export function getScoreColor(score: number): string {
  if (score >= 70) return 'green.600';
  if (score >= 50) return 'yellow.600';
  return 'red.600';
}

/**
 * Get background color for AI score based on tier
 * @param score - AI score (0-100)
 * @returns Chakra UI background color string
 */
export function getScoreBgColor(score: number): string {
  if (score >= 70) return 'green.100';
  if (score >= 50) return 'yellow.100';
  return 'red.100';
}

/**
 * Get color for AI score (alternative function name used in some components)
 * @param score - AI score (0-100)
 * @returns Chakra UI color string
 */
export function getAiScoreColor(score: number): string {
  return getScoreColor(score);
}
